import React, { useCallback, useRef } from "react";
import { classNames } from "../styles/classNames";
import ResizeHandle from "./ResizeHandle";
import { useStoredSize } from "./useStoredSize";

interface ResizableSplitPaneProps {
  left: React.ReactNode;
  right: React.ReactNode;
  /** `false` shows the left pane alone, in the same place, so it keeps its state. */
  rightShown?: boolean;
  ariaLabel: string;
  storageKey: string;
  defaultLeftWidth?: number;
  minLeftWidth?: number;
  maxLeftWidth?: number;
  minLeftPaneSize?: string;
  keyboardStep?: number;
  className?: string;
  leftPaneClassName?: string;
  rightPaneClassName?: string;
}

const DEFAULT_LEFT_WIDTH = 50;
const DEFAULT_MIN_LEFT_WIDTH = 24;
const DEFAULT_MAX_LEFT_WIDTH = 70;
const DEFAULT_KEYBOARD_STEP = 4;

/** Two panes side by side, the left one's width in percent, set by the one resize handle and kept per `storageKey`. */
export default function ResizableSplitPane({
  left,
  right,
  rightShown = true,
  ariaLabel,
  storageKey,
  defaultLeftWidth = DEFAULT_LEFT_WIDTH,
  minLeftWidth = DEFAULT_MIN_LEFT_WIDTH,
  maxLeftWidth = DEFAULT_MAX_LEFT_WIDTH,
  minLeftPaneSize = "220px",
  keyboardStep = DEFAULT_KEYBOARD_STEP,
  className,
  leftPaneClassName,
  rightPaneClassName,
}: ResizableSplitPaneProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftWidth, setLeftWidth] = useStoredSize(storageKey, { initial: defaultLeftWidth, min: minLeftWidth, max: maxLeftWidth });
  const single = !rightShown;
  const widthAt = useCallback((clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    return rect === undefined || rect.width === 0 ? leftWidth : ((clientX - rect.left) / rect.width) * 100;
  }, [leftWidth]);

  return (
    <div
      ref={containerRef}
      className={classNames(
        "grid",
        !single && "gap-4 lg:grid-cols-[minmax(var(--split-left-min),var(--split-left-width))_12px_minmax(0,1fr)] lg:gap-0",
        className
      )}
      style={
        {
          "--split-left-width": `${String(leftWidth)}%`,
          "--split-left-min": minLeftPaneSize,
        } as React.CSSProperties
      }
    >
      <div className={classNames("min-w-0", leftPaneClassName)}>{left}</div>
      {single ? null : (
        <>
          <ResizeHandle
            label={ariaLabel}
            value={leftWidth}
            min={minLeftWidth}
            max={maxLeftWidth}
            step={keyboardStep}
            valueAt={widthAt}
            onChange={setLeftWidth}
            look="bar"
            className="hidden w-1 rounded-full lg:block lg:justify-self-center"
          />
          <div className={classNames("min-w-0", rightPaneClassName)}>{right}</div>
        </>
      )}
    </div>
  );
}
